import { Box } from '@mui/material';
import { DateTime } from 'luxon';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Card } from '@work4all/components/lib/dataDisplay/card';
import { NumberInput } from '@work4all/components/lib/input/number-input';
import { TimeInputPicker } from '@work4all/components/lib/input/time-input-picker';

import { ComesGoesRounding } from '@work4all/models/lib/Enums/ComesGoesRounding.enum';
import { TypeOfWorkTimeTracking } from '@work4all/models/lib/Enums/TypeOfWorkTimeTracking.enum';

import { EnumPickerField } from '../../../../../../../components/entity-picker/EnumPickerField';
import { PauseRulePickerField } from '../../../../../../../components/entity-picker/PauseRulePickerField';
import { ControllerPlus } from '../../../../../form-plus/controller-plus';
import { useFormContextPlus } from '../../../../../form-plus/use-form-context-plus';
import { CheckboxRadioItem } from '../../../../../locked-inputs';
import { Collapse, ControlWrapper, Panel } from '../../../../components';
import { UserFormValue } from '../../types';

import { getTimeValue } from './getTimeValue';

const generateDayLabels = () => {
  return Array.from({ length: 7 }, (_, i) => i + 1)
    .map((x) =>
      DateTime.fromFormat(`2024.01.0${x}`, 'yyyy.MM.dd').toFormat('cccc')
    )
    .map((x) => x.slice(0, 2) + '.');
};

export const WorkTimeTabPanel = () => {
  const { i18n, t } = useTranslation();
  const { register, control } = useFormContextPlus<UserFormValue>();

  const dayLabels = useMemo(() => {
    return generateDayLabels();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n.language]);

  return (
    <Panel style={{ height: '100%' }}>
      <Collapse defaultOpen title={t('USER.CALCULATION_OF_WORK_HOURS')}>
        <Card>
          <ControlWrapper columns={3}>
            <ControllerPlus
              name="timestampTargetMinutesPerDay"
              control={control}
              render={({ field }) => {
                return (
                  <NumberInput
                    {...field}
                    label={t('INPUTS.DAILY_TARGET')}
                    hideControls
                    unit="Min."
                  />
                );
              }}
            />
            <ControlWrapper gridColumn="span 2" columns={7}>
              <Box
                display="grid"
                gridTemplateColumns="1fr 1fr 1fr 1fr 1fr"
                gridColumn="span 5"
              >
                <ControllerPlus
                  name="dailyTargetWorkHoursMonday"
                  control={control}
                  render={({ field }) => {
                    return (
                      <NumberInput
                        {...field}
                        label={dayLabels[0]}
                        hideControls
                        multientry="left"
                      />
                    );
                  }}
                />
                <ControllerPlus
                  name="dailyTargetWorkHoursTuesday"
                  control={control}
                  render={({ field }) => {
                    return (
                      <NumberInput
                        {...field}
                        label={dayLabels[1]}
                        hideControls
                        multientry="both"
                      />
                    );
                  }}
                />
                <ControllerPlus
                  name="dailyTargetWorkHoursWednesday"
                  control={control}
                  render={({ field }) => {
                    return (
                      <NumberInput
                        {...field}
                        label={dayLabels[2]}
                        hideControls
                        multientry="both"
                      />
                    );
                  }}
                />
                <ControllerPlus
                  name="dailyTargetWorkHoursThursday"
                  control={control}
                  render={({ field }) => {
                    return (
                      <NumberInput
                        {...field}
                        label={dayLabels[3]}
                        hideControls
                        multientry="both"
                      />
                    );
                  }}
                />
                <ControllerPlus
                  name="dailyTargetWorkHoursFriday"
                  control={control}
                  render={({ field }) => {
                    return (
                      <NumberInput
                        {...field}
                        label={dayLabels[4]}
                        hideControls
                        multientry="right"
                      />
                    );
                  }}
                />
              </Box>
              <ControlWrapper gap={'0'} gridColumn="span 2" columns={2}>
                <ControllerPlus
                  name="dailyTargetWorkHoursSaturday"
                  control={control}
                  render={({ field }) => {
                    return (
                      <NumberInput
                        {...field}
                        label={dayLabels[5]}
                        hideControls
                        multientry="left"
                      />
                    );
                  }}
                />
                <ControllerPlus
                  name="dailyTargetWorkHoursSunday"
                  control={control}
                  render={({ field }) => {
                    return (
                      <NumberInput
                        {...field}
                        label={dayLabels[6]}
                        hideControls
                        multientry="right"
                      />
                    );
                  }}
                />
              </ControlWrapper>
            </ControlWrapper>
          </ControlWrapper>
          <ControlWrapper columns={3}>
            <ControllerPlus
              name="maxWorkingHours"
              control={control}
              render={({ field }) => {
                return (
                  <NumberInput
                    {...field}
                    label={t('INPUTS.MAX_WORKING_HOURS')}
                    hideControls
                    unit="Min."
                  />
                );
              }}
            />
            <ControlWrapper gridColumn="span 2" columns={7}>
              <Box
                display="grid"
                gridTemplateColumns="1fr 1fr 1fr 1fr 1fr"
                gridColumn="span 5"
              >
                <ControllerPlus
                  name="maxWorkHoursMonday"
                  control={control}
                  render={({ field }) => {
                    return (
                      <NumberInput
                        {...field}
                        label={dayLabels[0]}
                        hideControls
                        multientry="left"
                      />
                    );
                  }}
                />
                <ControllerPlus
                  name="maxWorkHoursTuesday"
                  control={control}
                  render={({ field }) => {
                    return (
                      <NumberInput
                        {...field}
                        label={dayLabels[1]}
                        hideControls
                        multientry="both"
                      />
                    );
                  }}
                />
                <ControllerPlus
                  name="maxWorkHoursWednesday"
                  control={control}
                  render={({ field }) => {
                    return (
                      <NumberInput
                        {...field}
                        label={dayLabels[2]}
                        hideControls
                        multientry="both"
                      />
                    );
                  }}
                />
                <ControllerPlus
                  name="maxWorkHoursThursday"
                  control={control}
                  render={({ field }) => {
                    return (
                      <NumberInput
                        {...field}
                        label={dayLabels[3]}
                        hideControls
                        multientry="both"
                      />
                    );
                  }}
                />
                <ControllerPlus
                  name="maxWorkHoursFriday"
                  control={control}
                  render={({ field }) => {
                    return (
                      <NumberInput
                        {...field}
                        label={dayLabels[4]}
                        hideControls
                        multientry="right"
                      />
                    );
                  }}
                />
              </Box>
              <ControlWrapper gap={'0'} gridColumn="span 2" columns={2}>
                <ControllerPlus
                  name="maxWorkHoursSaturday"
                  control={control}
                  render={({ field }) => {
                    return (
                      <NumberInput
                        {...field}
                        label={dayLabels[5]}
                        hideControls
                        multientry="left"
                      />
                    );
                  }}
                />
                <ControllerPlus
                  name="maxWorkHoursSunday"
                  control={control}
                  render={({ field }) => {
                    return (
                      <NumberInput
                        {...field}
                        label={dayLabels[6]}
                        hideControls
                        multientry="right"
                      />
                    );
                  }}
                />
              </ControlWrapper>
            </ControlWrapper>
          </ControlWrapper>
          <ControlWrapper columns={3}>
            <ControllerPlus
              name="vacationValue"
              control={control}
              render={({ field }) => {
                return (
                  <NumberInput
                    {...field}
                    label={t('INPUTS.VARIANT_VACATION_TIME_VALUE')}
                    hideControls
                    unit="h"
                  />
                );
              }}
            />
            <ControllerPlus
              control={control}
              name="timestampRecordingVariant"
              render={({ field }) => {
                return (
                  <EnumPickerField
                    values={Object.values(TypeOfWorkTimeTracking)}
                    label={t('INPUTS.CAPTURE_DEVICE')}
                    {...field}
                  />
                );
              }}
            />
          </ControlWrapper>
          <ControlWrapper columns={3}>
            <ControllerPlus
              name="timing"
              control={control}
              render={({ field }) => {
                return (
                  <NumberInput
                    {...field}
                    label={t('INPUTS.TIMING')}
                    hideControls
                    unit="Min."
                  />
                );
              }}
            />
            <CheckboxRadioItem
              {...register('markHolidaysAsWorkingHours')}
              label={t('INPUTS.TREAT_PUBLIC_HOLIDAYS_AS_WORKING_HOURS')}
            />
          </ControlWrapper>
          <ControlWrapper columns={3}>
            <ControlWrapper columns={2} gap="0" paddingBottom={false}>
              <ControllerPlus
                control={control}
                name="timestampRoundingMorning"
                render={({ field }) => {
                  return (
                    <EnumPickerField
                      values={Object.values(ComesGoesRounding)}
                      label={t('INPUTS.START_ROUND')}
                      {...field}
                    />
                  );
                }}
              />
              <ControllerPlus
                control={control}
                name="timestampRoundingEvening"
                render={({ field }) => {
                  return (
                    <EnumPickerField
                      values={Object.values(ComesGoesRounding)}
                      label={t('INPUTS.END_ROUND')}
                      {...field}
                    />
                  );
                }}
              />
            </ControlWrapper>

            <ControlWrapper columns={2} gap="0" paddingBottom={false}>
              <ControllerPlus
                control={control}
                name="timestampRoundingFromTime"
                render={({ field }) => {
                  return (
                    <EnumPickerField
                      values={Object.values(ComesGoesRounding)}
                      label={t('INPUTS.ROUNDING')}
                      {...field}
                    />
                  );
                }}
              />
              <ControllerPlus
                name="roundingForHour"
                control={control}
                render={({ field }) => {
                  const value = getTimeValue(field.value);
                  return (
                    <TimeInputPicker
                      label={t('INPUTS.AT')}
                      value={value}
                      onChange={(e) => {
                        const value = e.currentTarget?.value;

                        if (value) {
                          field.onChange(value);
                        }
                      }}
                    />
                  );
                }}
              />
            </ControlWrapper>

            <ControllerPlus
              name="earliestCommingTime"
              control={control}
              render={({ field }) => {
                const value = getTimeValue(field.value);
                return (
                  <TimeInputPicker
                    label={t('INPUTS.EARLIEST_START')}
                    value={value}
                    onChange={(e) => {
                      const value = e.currentTarget?.value;

                      if (value) {
                        field.onChange(value);
                      }
                    }}
                  />
                );
              }}
            />
          </ControlWrapper>
        </Card>
      </Collapse>
      <Collapse defaultOpen title={t('USER.CALCULATION_OF_OVERTIME')}>
        <Card>
          <ControlWrapper columns={3}>
            <ControllerPlus
              name="timestampIncludedMinutesPerDay"
              control={control}
              render={({ field }) => {
                return (
                  <NumberInput
                    {...field}
                    label={t('INPUTS.INCLUDED_PER_DAY')}
                    hideControls
                    unit="Min."
                  />
                );
              }}
            />
            <ControllerPlus
              name="comesGoesInclWeek"
              control={control}
              render={({ field }) => {
                return (
                  <NumberInput
                    {...field}
                    label={t('INPUTS.INCLUDED_PER_WEEK')}
                    hideControls
                    unit="Min."
                  />
                );
              }}
            />
            <ControllerPlus
              name="timestampIncludedMinutesPerMonth"
              control={control}
              render={({ field }) => {
                return (
                  <NumberInput
                    {...field}
                    label={t('INPUTS.INCLUDED_PER_MONTH')}
                    hideControls
                    unit="Min."
                  />
                );
              }}
            />
          </ControlWrapper>
        </Card>
      </Collapse>
      <Collapse defaultOpen title={t('USER.PAUSE')}>
        <Card>
          <ControlWrapper columns={3}>
            <ControllerPlus
              control={control}
              name="workTimeTrackingBreakRule"
              render={({ field }) => {
                return <PauseRulePickerField multiple={false} {...field} />;
              }}
            />
            <ControllerPlus
              name="comesGoesMinimalPause"
              control={control}
              render={({ field }) => {
                return (
                  <NumberInput
                    {...field}
                    label={t('INPUTS.MINIMAL_BREAK')}
                    hideControls
                    unit="Min."
                  />
                );
              }}
            />
          </ControlWrapper>
        </Card>
      </Collapse>
    </Panel>
  );
};
